'use client'
import clsx from 'clsx'
import Image from 'next/image'
import {
    ChangeEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
    onSearch: (term: string) => void
    debounce?: number
    minChars?: number
    className?: string
    placeholder?: string
    hideInput?: boolean
    initSearch?: string
    'data-testid'?: string
}

export default function EshopSearch({
    onSearch,
    debounce = 0,
    minChars = 3,
    className,
    placeholder,
    hideInput = false,
    initSearch,
}: Props) {
    const [search, setSearch] = useState(initSearch || '')
    const { t } = useTranslation()
    const timeout = useRef<NodeJS.Timeout | null>(null)

    const handleSearch: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            setSearch(e.target.value)
            timeout.current && clearTimeout(timeout.current)
            if (
                minChars &&
                e.target.value.length > 0 &&
                e.target.value.length < minChars
            ) {
                return
            }
            if (debounce > 0) {
                timeout.current = setTimeout(() => {
                    onSearch(e.target.value)
                }, debounce)
            } else {
                onSearch(e.target.value)
            }
        },
        [debounce, minChars, onSearch]
    )

    useEffect(() => {
        if (typeof initSearch !== 'undefined') {
            setSearch(initSearch)
        }
    }, [initSearch])

    return (
        <div
            className={clsx(
                'flex border-b border-gray-light2 pb-3.5',
                className
            )}
        >
            <div className="flex items-center">
                <Image
                    src="/search.svg"
                    className="flex-none grow-0"
                    alt="search icon"
                    width={24}
                    height={24}
                />
            </div>
            {!hideInput && (
                <input
                    className={clsx(
                        'grow p-1',
                        search.length > 0 ? 'mx-4' : 'ml-4'
                    )}
                    type="text"
                    value={search}
                    onChange={handleSearch}
                    placeholder={placeholder || t('eshop.search.placeholder')}
                    data-testid="mainSearchInput"
                    suppressHydrationWarning
                />
            )}
            {search.length > 0 && (
                <div className="flex items-center">
                    <Image
                        src="/cross.svg"
                        alt="clear icon"
                        className="flex-none grow-0 cursor-pointer p-1"
                        width={24}
                        height={24}
                        onClick={() => {
                            setSearch('')
                            onSearch('')
                        }}
                    />
                </div>
            )}
        </div>
    )
}
